import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const STORAGE_KEY = 'formcraft_builder_tooltips_dismissed';

const TOOLTIPS = [
  {
    id: 'drag-drop',
    icon: 'MousePointer2',
    title: 'Drag & Drop Elements',
    description: 'Drag any field from the left panel onto the canvas, or click it to add instantly.',
    position: 'canvas-top',
    accentColor: '#007876',
  },
  {
    id: 'configure-fields',
    icon: 'Settings2',
    title: 'Configure Fields',
    description: 'Click any element on the canvas to open its settings — labels, validation, placeholders, and more.',
    position: 'canvas-mid',
    accentColor: '#007876',
  },
  {
    id: 'conditional-logic',
    icon: 'GitBranch',
    title: 'Conditional Logic',
    description: 'Use the "Conditional Logic" tab above to show or hide fields based on user answers.',
    position: 'toolbar',
    accentColor: '#007876',
  },
  {
    id: 'publish',
    icon: 'Upload',
    title: 'Publish Your Form',
    description: 'When ready, click Publish to get a shareable link and embed code for your form.',
    position: 'publish-btn',
    accentColor: '#007876',
  },
];

const TooltipCard = ({ tooltip, index, total, onDismiss, onNext, onDismissAll }) => {
  return (
    <div
      className="relative bg-white rounded-xl shadow-lg border border-gray-100 p-4 w-72 animate-fade-in"
      style={{
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      }}
    >
      {/* Accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
        style={{ background: tooltip?.accentColor }}
      />
      {/* Header */}
      <div className="flex items-start justify-between mt-1 mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${tooltip?.accentColor}18` }}
          >
            <Icon name={tooltip?.icon} size={15} style={{ color: tooltip?.accentColor }} />
          </div>
          <span className="text-sm font-semibold text-gray-800">{tooltip?.title}</span>
        </div>
        <button
          onClick={onDismissAll}
          className="p-0.5 rounded hover:bg-gray-100 transition-colors flex-shrink-0 ml-2"
          title="Dismiss all tips"
        >
          <Icon name="X" size={14} color="#9ca3af" />
        </button>
      </div>
      {/* Body */}
      <p className="text-xs text-gray-500 leading-relaxed mb-3">{tooltip?.description}</p>
      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400 font-medium">
          {index + 1} of {total}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onDismissAll}
            className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip all
          </button>
          {index < total - 1 ? (
            <button
              onClick={onNext}
              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white transition-colors"
              style={{ background: tooltip?.accentColor }}
            >
              Next <Icon name="ChevronRight" size={12} />
            </button>
          ) : (
            <button
              onClick={onDismiss}
              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white transition-colors"
              style={{ background: tooltip?.accentColor }}
            >
              Got it! <Icon name="Check" size={12} />
            </button>
          )}
        </div>
      </div>
      {/* Step dots */}
      <div className="flex items-center gap-1 mt-2 justify-center">
        {Array.from({ length: total })?.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all"
            style={{
              width: i === index ? 16 : 6,
              height: 6,
              background: i === index ? tooltip?.accentColor : '#e5e7eb',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const NewUserTooltips = ({ isNewForm, formElements }) => {
  const [dismissed, setDismissed] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [positions, setPositions] = useState({});
  const containerRef = useRef(null);

  // Check if already dismissed
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') {
      setDismissed(true);
    }
  }, []);

  // Only show for new forms or forms with no elements
  const shouldShow = !dismissed && (isNewForm || formElements?.length === 0);

  const handleDismissAll = () => {
    setDismissed(true);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, TOOLTIPS?.length - 1));
  };

  const handleDismiss = () => {
    handleDismissAll();
  };

  if (!shouldShow) return null;

  const tooltip = TOOLTIPS?.[currentIndex];

  // Position mapping: where each tooltip card appears
  const positionStyles = {
    'canvas-top': {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
    },
    'canvas-mid': {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
    },
    'toolbar': {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
    },
    'publish-btn': {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
    },
  };

  return (
    <>
      {/* Subtle backdrop — only a very light overlay, not blocking */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 9998, background: 'rgba(0,0,0,0.04)' }}
      />
      {/* Tooltip card */}
      <div style={positionStyles?.[tooltip?.position] || positionStyles?.['canvas-top']}>
        <TooltipCard
          tooltip={tooltip}
          index={currentIndex}
          total={TOOLTIPS?.length}
          onDismiss={handleDismiss}
          onNext={handleNext}
          onDismissAll={handleDismissAll}
        />
      </div>
    </>
  );
};

export default NewUserTooltips;
